// Estatísticas por plano (lote 6, item 3), a partir do seu perfil. O servidor decide o que chega pelo plano:
// - Básicas (todos): partidas, vitórias, aproveitamento, ofensiva, Rodagem e as últimas 10.
// - Completas (Bronze e Prata): por tipo de sala; truco pedido, aceito, corrido e ganho; mão de onze jogada, corrida e
//   ganha; mão de ferro; Zap jogado e vencido; lances; parceiros e o melhor parceiro (com 10 partidas juntos ou mais).
//   "Contando desde" a primeira partida gravada.
// - Com histórico (Ouro): as últimas 12 semanas, as temporadas e o histórico completo com filtro por sala, trio, resultado
//   e período.
// O nível que o plano não tem aparece com cadeado e o atalho para Planos, sem número nenhum.
import { useEffect, useState } from 'react';
import {
  type EstatisticasDoJogador, LANCES_DO_PERFIL, MINIMO_MELHOR_PARCEIRO, NOME_DO_LANCE, type Parceiro, type PartidaDoRetrospecto, type Ponto,
  type TipoDeSalaNoRetrospecto,
} from '@truco6/gamificacao';
import { MATRIZ, NOME_DO_PLANO, PLANOS, type Plano } from '@truco6/planos';
import { api, lerTrios } from '../comum/api.ts';
import type { Sessao } from '../comum/conta.ts';
import { QuadrosDoRetrospecto, rotuloDaSala } from '../comum/Retrospecto.tsx';
import { ir, voltar } from '../comum/rotas.ts';
import { Rosto } from '../comum/Rosto.tsx';
import { Carregando, Tela, Topo } from './comum.tsx';

interface PaginaDoHistorico { partidas: PartidaDoRetrospecto[]; total: number; pagina: number; paginas: number }
// presencial: as noites do marcador presencial, só no Ouro ("Truco presencial").
type Estatisticas = EstatisticasDoJogador & { presencial?: { noites: number; partidas: number } | null };
const lerEstatisticas = (s: Sessao) => api<Estatisticas>(s, 'GET', '/estatisticas');
const lerHistorico = (s: Sessao, q: URLSearchParams) => api<PaginaDoHistorico>(s, 'GET', '/estatisticas/historico?' + q);

const FUSO = 'America/Sao_Paulo';
const DATA = new Intl.DateTimeFormat('pt-BR', { timeZone: FUSO, day: '2-digit', month: '2-digit', year: 'numeric' });
const DIA_MES = new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC', day: '2-digit', month: '2-digit' });
const NOME_DO_TIPO: Record<TipoDeSalaNoRetrospecto, string> = { liga: 'Liga', publica: 'Sala pública', privada: 'Sala privada', tentos: 'Valendo Tentos', campeonato: 'Campeonato' };

// "Bronze, Prata e Ouro": os planos que têm pelo menos o nível pedido.
function planosCom(nivel: 'completas' | 'historico'): string {
  const ps = PLANOS.filter((p) => nivel === 'historico' ? MATRIZ.estatisticas.valores[p] === 'historico' : MATRIZ.estatisticas.valores[p] !== 'basicas').map((p) => NOME_DO_PLANO[p]);
  return ps.length > 1 ? `${ps.slice(0, -1).join(', ')} e ${ps.at(-1)}` : ps[0] ?? '';
}
// Sem jogadas, não há porcentagem: o detalhe some.
const porcento = (v: number, p: number) => (p ? `${Math.round((v / p) * 100)}%` : undefined);
// "2026-T4" vira "4ª temporada de 2026".
const nomeDaTemporada = (t: string) => { const [a, q] = t.split('-T'); return `${q}ª temporada de ${a}`; };

// larga: no computador, a seção usa a linha toda da grade (o histórico completo).
function Secao({ titulo, children, rotulo, larga = false }: { titulo: string; children: React.ReactNode; rotulo?: string; larga?: boolean }) {
  return (
    <section className={'cartao secao-perfil secao-estatisticas' + (larga ? ' secao-larga' : '')} aria-label={rotulo ?? titulo}>
      <h2 className="rotulo">{titulo.toUpperCase()}</h2>
      {children}
    </section>
  );
}

function Numero({ rotulo, valor, detalhe }: { rotulo: string; valor: string | number; detalhe?: string }) {
  return <div><dt>{rotulo}</dt><dd>{valor}{detalhe && <small>{detalhe}</small>}</dd></div>;
}

function Travado({ titulo, texto, itens, plano }: { titulo: string; texto: string; itens: string[]; plano: Plano }) {
  return (
    <Secao titulo={titulo} rotulo={titulo + ', com cadeado'}>
      <ul className="itens-travados" aria-label="O que entra">{itens.map((x) => <li key={x}>{x}</li>)}</ul>
      <div className="cadeado-tentos">
        <p className="cadeado">{texto}</p>
        <button className="btn2 dourado" onClick={() => ir({ tela: 'planos', plano, ciclo: 'mensal' })}>Ver planos</button>
      </div>
    </Secao>
  );
}

function LinhaParceiro({ p, melhor }: { p: Parceiro; melhor?: boolean }) {
  return (
    <li className={melhor ? 'melhor' : undefined}>
      <Rosto perfil={{ apelido: p.apelido, avatar: p.avatar }} usuario={p.usuario} cor={melhor ? 'var(--gold-400)' : 'var(--line-600)'} tamanho={36} />
      <span className="nome"><b>{p.apelido}</b><small>{p.partidas} {p.partidas === 1 ? 'partida' : 'partidas'} juntos · {p.vitorias} V</small></span>
      <b className="aprov">{p.aproveitamento}%</b>
    </li>
  );
}

// Barras do aproveitamento por semana, da mais antiga para a de agora. Semana sem partida fica só com o traço.
function Evolucao({ semanas }: { semanas: Ponto[] }) {
  const resumo = semanas.filter((s) => s.partidas).map((s) => `semana de ${DIA_MES.format(new Date(s.chave + 'T12:00:00Z'))}: ${s.partidas} partidas, ${s.aproveitamento}%`).join('; ');
  return (
    <figure className="evolucao" aria-label={resumo ? `Aproveitamento por semana. ${resumo}.` : 'Nenhuma partida nas últimas 12 semanas.'}>
      <div className="barras" aria-hidden="true">
        {semanas.map((s) => (
          <span key={s.chave} className={'barra' + (s.partidas ? '' : ' vazia')} title={`${s.partidas} partidas`}>
            {s.partidas > 0 && <small>{s.aproveitamento}</small>}
            <i style={{ height: s.partidas ? `${Math.max(5, s.aproveitamento * 0.8)}%` : undefined }} />
          </span>
        ))}
      </div>
      <figcaption className="pe-retrospecto"><small>{DIA_MES.format(new Date(semanas[0]!.chave + 'T12:00:00Z'))}</small><small>esta semana</small></figcaption>
    </figure>
  );
}

function HistoricoCompleto({ sessao }: { sessao: Sessao }) {
  const [trios, setTrios] = useState<{ id: string; nome: string }[]>([]);
  const [trio, setTrio] = useState('');
  const [tipo, setTipo] = useState('');
  const [resultado, setResultado] = useState('');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [pagina, setPagina] = useState(1);
  const [p, setP] = useState<PaginaDoHistorico | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    if (!sessao.anonimo) lerTrios(sessao).then((t) => { if (vivo) setTrios(t.trios.map((x) => ({ id: x.id, nome: x.nome }))); }, () => {});
    return () => { vivo = false; };
  }, [sessao.id]);
  useEffect(() => {
    let vivo = true;
    const q = new URLSearchParams({ pagina: String(pagina) });
    if (tipo) q.set('tipo', tipo);
    if (trio) q.set('trio', trio);
    if (resultado) q.set('resultado', resultado);
    if (de) q.set('de', de);
    if (ate) q.set('ate', ate);
    setErro(null);
    lerHistorico(sessao, q).then((x) => { if (vivo) setP(x); }, (e: Error) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [tipo, trio, resultado, de, ate, pagina]);
  const mudar = (f: (v: string) => void) => (ev: { target: { value: string } }) => { f(ev.target.value); setPagina(1); };
  return (
    <div className="historico-completo">
      <div className="filtros-historico">
        <label className="campo"><span>Sala</span>
          <select aria-label="Tipo de sala" value={tipo} onChange={mudar(setTipo)}>
            <option value="">Todas</option>
            {(Object.keys(NOME_DO_TIPO) as TipoDeSalaNoRetrospecto[]).map((t) => <option key={t} value={t}>{NOME_DO_TIPO[t]}</option>)}
          </select>
        </label>
        {trios.length > 0 && (
          <label className="campo"><span>Trio</span>
            <select aria-label="Trio fixo" value={trio} onChange={mudar(setTrio)}>
              <option value="">Todos</option>
              {trios.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
            </select>
          </label>
        )}
        <label className="campo"><span>Resultado</span>
          <select aria-label="Resultado" value={resultado} onChange={mudar(setResultado)}>
            <option value="">Todos</option><option value="V">Vitórias</option><option value="D">Derrotas</option>
          </select>
        </label>
        <label className="campo"><span>De</span><input type="date" value={de} max={ate || undefined} onChange={mudar(setDe)} /></label>
        <label className="campo"><span>Até</span><input type="date" value={ate} min={de || undefined} onChange={mudar(setAte)} /></label>
      </div>
      {erro && <p className="erro" role="alert">{erro}</p>}
      {!p && !erro && <p className="carregando" role="status">Carregando...</p>}
      {p && (
        <>
          <p className="nota" role="status">{p.total === 0 ? 'Nenhuma partida com esses filtros.' : `${p.total} ${p.total === 1 ? 'partida' : 'partidas'}`}</p>
          <ol className="partidas-retrospecto partidas-historico">
            {p.partidas.map((x) => (
              <li key={x.partida}>
                <div className="cab-partida">
                  <span className={'quadro ' + (x.resultado === 'V' ? 'vitoria' : 'derrota')} aria-hidden="true">{x.resultado === 'V' ? '✓' : '✕'}</span>
                  <span className="quem-partida">
                    <b>{x.resultado === 'V' ? 'Vitória' : 'Derrota'}{x.detalhe ? ` · ${x.detalhe.placar[0]} x ${x.detalhe.placar[1]}` : ''}</b>
                    <small>{DATA.format(new Date(x.terminadaEm))} · {rotuloDaSala(x)}</small>
                    {x.detalhe && x.detalhe.parceiros.length > 0 && <small>Com {x.detalhe.parceiros.map((y) => y.apelido).join(' e ')}</small>}
                  </span>
                </div>
              </li>
            ))}
          </ol>
          {p.paginas > 1 && (
            <div className="dupla paginas-historico">
              <button className="btn2" disabled={p.pagina <= 1} onClick={() => setPagina(p.pagina - 1)}>Mais novas</button>
              <span className="nota">{p.pagina} de {p.paginas}</span>
              <button className="btn2" disabled={p.pagina >= p.paginas} onClick={() => setPagina(p.pagina + 1)}>Mais antigas</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function EstatisticasTela({ sessao }: { sessao: Sessao }) {
  const [e, setE] = useState<Estatisticas | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [historicoAberto, setHistoricoAberto] = useState(false);
  useEffect(() => {
    let vivo = true;
    lerEstatisticas(sessao).then((x) => { if (vivo) setE(x); }, (err: Error) => { if (vivo) setErro(err.message); });
    return () => { vivo = false; };
  }, [sessao.id]);

  if (erro) return <Tela><Topo titulo="Estatísticas" voltar={voltar} /><p className="erro" role="alert">{erro}</p></Tela>;
  if (!e) return <Tela><Topo titulo="Estatísticas" voltar={voltar} /><Carregando /></Tela>;
  const b = e.basicas, c = e.completas, h = e.historico;
  const lances = c ? LANCES_DO_PERFIL.filter((l) => (c.lances[l] ?? 0) > 0) : [];
  const outros = c ? c.parceiros.filter((p) => p.usuario !== c.melhorParceiro?.usuario) : []; // o melhor fica em destaque, uma vez só
  return (
    <Tela painel>
      <Topo titulo="Estatísticas" voltar={voltar} />
      <div className="grade-estatisticas">
      <Secao titulo="Básicas">
        <dl className="numeros-perfil numeros-estatisticas">
          <Numero rotulo="Partidas" valor={b.partidas} />
          <Numero rotulo="Vitórias" valor={b.vitorias} />
          <Numero rotulo="Aproveitamento" valor={`${b.aproveitamento}%`} />
          <Numero rotulo="Ofensiva" valor={b.ofensiva} detalhe={b.ofensiva === 1 ? 'dia' : 'dias'} />
          <Numero rotulo="Rodagem" valor={`Nível ${b.rodagem.nivel}`} />
        </dl>
        {b.ultimas.length > 0 ? (
          <div className="retrospecto estatico" aria-label={`Últimas ${b.ultimas.length}: ${b.ultimas.filter((p) => p.resultado === 'V').length} vitórias`}>
            <span className="cab-retrospecto"><span className="rotulo">ÚLTIMAS 10</span><b>{b.ultimas.filter((p) => p.resultado === 'V').length} V · {b.ultimas.filter((p) => p.resultado === 'D').length} D</b></span>
            <QuadrosDoRetrospecto partidas={b.ultimas} />
            <span className="pe-retrospecto"><small>mais antiga</small><small>última</small></span>
          </div>
        ) : <p className="nota">Ainda sem partidas</p>}
      </Secao>

      {!c ? (
        <Travado titulo="Completas" plano="bronze" texto={`As estatísticas completas são dos planos ${planosCom('completas')}.`}
          itens={['Por tipo de sala', 'Truco pedido, aceito, corrido e ganho', 'Mão de onze e mão de ferro', 'Zap e lances', 'Parceiros e o melhor parceiro']} />
      ) : (
        <>
          <p className="nota contando-desde">{c.contandoDesde ? `Truco, mão de onze, mão de ferro e Zap contando desde ${DATA.format(new Date(c.contandoDesde))}.` : 'Truco, mão de onze, mão de ferro e Zap contam a partir da próxima partida.'}</p>
          {c.porTipo.length > 0 && (
            <Secao titulo="Por tipo de sala">
              <ul className="linhas-estatisticas">
                {c.porTipo.map((t) => (
                  <li key={t.tipo}><span>{NOME_DO_TIPO[t.tipo]}</span><small>{t.partidas} {t.partidas === 1 ? 'partida' : 'partidas'} · {t.vitorias} V</small><b>{t.aproveitamento}%</b></li>
                ))}
              </ul>
            </Secao>
          )}
          <Secao titulo="Truco">
            <dl className="numeros-perfil numeros-estatisticas quatro">
              <Numero rotulo="Pedidos" valor={c.truco.pedido} />
              <Numero rotulo="Aceitos" valor={c.truco.aceito} />
              <Numero rotulo="Corridos" valor={c.truco.corrido} />
              <Numero rotulo="Ganhos" valor={c.truco.ganho} />
            </dl>
          </Secao>
          <Secao titulo="Mãos especiais e Zap">
            <dl className="numeros-perfil numeros-estatisticas">
              <Numero rotulo="Mão de onze" valor={`${c.onze.ganhas}/${c.onze.jogadas}`} detalhe={porcento(c.onze.ganhas, c.onze.jogadas)} />
              <Numero rotulo="Onze corrida" valor={c.onze.corridas} />
              <Numero rotulo="Mão de ferro" valor={`${c.ferro.ganhas}/${c.ferro.jogadas}`} detalhe={porcento(c.ferro.ganhas, c.ferro.jogadas)} />
              <Numero rotulo="Zap jogado" valor={c.zap.jogado} />
              <Numero rotulo="Zap vencido" valor={c.zap.vencido} detalhe={porcento(c.zap.vencido, c.zap.jogado)} />
            </dl>
            <p className="nota">Mão de onze e mão de ferro: ganhas de jogadas. Zap vencido: o Zap que levou a rodada.</p>
          </Secao>
          {lances.length > 0 && (
            <Secao titulo="Lances">
              <ul className="lances-perfil">{lances.map((l) => <li key={l}><span>{NOME_DO_LANCE[l]}</span><b>{c.lances[l]}x</b></li>)}</ul>
            </Secao>
          )}
          <Secao titulo="Parceiros">
            {c.melhorParceiro ? (
              <>
                <small className="rotulo-melhor">MELHOR PARCEIRO</small>
                <ul className="parceiros-estatisticas"><LinhaParceiro p={c.melhorParceiro} melhor /></ul>
              </>
            ) : <p className="nota">O melhor parceiro aparece com {MINIMO_MELHOR_PARCEIRO} partidas juntos.</p>}
            {outros.length > 0 && <ul className="parceiros-estatisticas">{outros.map((p) => <LinhaParceiro key={p.usuario} p={p} />)}</ul>}
            {!c.parceiros.length && <p className="nota">Ainda sem parceiros de trio.</p>}
          </Secao>
        </>
      )}

      {!h ? (
        <Travado titulo="Com histórico" plano="ouro" texto={`O histórico é do plano ${planosCom('historico')}.`}
          itens={['Evolução nas últimas 12 semanas', 'Cada temporada', 'Histórico completo com filtros']} />
      ) : (
        <>
          <Secao titulo="Últimas 12 semanas"><Evolucao semanas={h.semanas} /></Secao>
          {h.temporadas.length > 0 && (
            <Secao titulo="Temporadas">
              <ul className="linhas-estatisticas">
                {[...h.temporadas].reverse().map((t) => (
                  <li key={t.chave}><span>{nomeDaTemporada(t.chave)}</span><small>{t.partidas} {t.partidas === 1 ? 'partida' : 'partidas'} · {t.vitorias} V</small><b>{t.aproveitamento}%</b></li>
                ))}
              </ul>
            </Secao>
          )}
          {e.presencial && (
            <Secao titulo="Truco presencial">
              <dl className="numeros-perfil numeros-estatisticas">
                <Numero rotulo="Noites" valor={e.presencial.noites} />
                <Numero rotulo="Partidas marcadas" valor={e.presencial.partidas} />
              </dl>
              <p className="nota">As noites do marcador presencial. Não entram nos números de cima, porque ninguém confere a partida do bar.</p>
              <button className="link" onClick={() => ir({ tela: 'noites' })}>Ver as noites</button>
            </Secao>
          )}
          <Secao titulo="Histórico completo" larga>
            {historicoAberto ? <HistoricoCompleto sessao={sessao} /> : <button className="btn2" onClick={() => setHistoricoAberto(true)}>Ver todas as partidas</button>}
          </Secao>
        </>
      )}
      </div>
    </Tela>
  );
}
